import { ArrowLeft, Flag } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { sessionDuration } from '../../lib/duration.ts';
import { modeLabel } from '../../lib/modes.ts';
import type { HistoryItem } from '../../lib/training.ts';
import { listSessions } from '../../lib/training.ts';
import { ModeIcon } from './ModeIcon.tsx';

const STATUS_LABELS: Record<string, string> = {
    in_progress: 'en cours',
    completed: 'terminée',
    open: 'en cours',
    finished: 'terminée',
};

/** Deux décimales à la française pour les moyennes de putting, qui arrivent en
 *  chaîne : Postgres rend ses `numeric` ainsi, faute de pouvoir garantir la
 *  valeur exacte dans un double. */
const fr = (v: string | null, digits = 1): string =>
    v == null ? '—' : Number(v).toFixed(digits).replace('.', ',');

export function SessionHistory() {
    const [sessions, setSessions] = useState<HistoryItem[] | null>(null);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        void listSessions().then(setSessions).catch((e: Error) => setError(e.message));
    }, []);

    return (
        <div className="py-2">
            <Link to="/entrainement" className="btn btn-ghost btn-sm mb-2 gap-2">
                <ArrowLeft size={16} /> Entraînement
            </Link>
            <h1 className="text-2xl font-bold tracking-tight">Mes séances</h1>
            <p className="mt-1 text-sm opacity-60">
                Practice et putting, dans l'ordre où vous les avez faits.
            </p>

            {error && <div role="alert" className="alert alert-error mt-4 text-sm"><span>{error}</span></div>}

            {sessions?.length === 0 && (
                <p className="mt-6 rounded-box border border-dashed border-base-300 p-8 text-center text-sm opacity-60">
                    Aucune séance enregistrée pour l'instant.
                </p>
            )}

            <div className="mt-4 space-y-2">
                {sessions?.map((s) => {
                    const putting = s.kind === 'putting';
                    return (
                        <Link
                            key={`${s.kind}-${s.id}`}
                            to={putting ? `/putting/${s.id}` : `/entrainement/${s.id}`}
                            className="flex items-center gap-3 rounded-box bg-base-200 p-4 transition-colors hover:bg-base-300"
                        >
                            {/* L'icône du mode, à gauche : elle dit d'un coup
                                d'œil de quel exercice il s'agit, ce que la seule
                                date ne faisait pas depuis qu'il y en a
                                plusieurs. */}
                            <span className="shrink-0 text-primary">
                                {putting
                                    ? <Flag size={22} />
                                    : <ModeIcon mode={s.mode as 'triple_shot'} size={22} />}
                            </span>

                            <div className="min-w-0 flex-1">
                                <div className="truncate font-semibold">
                                    {putting ? (s.title ?? 'Putting') : modeLabel(s.mode)}
                                    <span className="ml-2 text-sm font-normal opacity-60">
                                        {new Date(s.startedAt).toLocaleDateString('fr-FR', {
                                            weekday: 'short', day: 'numeric', month: 'long',
                                        })}
                                    </span>
                                </div>

                                <div className="mt-0.5 truncate text-xs opacity-60">
                                    {putting ? (
                                        <>
                                            {s.count} trou{s.count > 1 ? 's' : ''}
                                            {s.playerCount != null && s.playerCount > 1
                                                && ` · ${s.playerCount} joueurs`}
                                            {s.position != null && s.playerCount != null
                                                && s.playerCount > 1 && ` · ${s.position}ᵉ`}
                                        </>
                                    ) : (
                                        <>
                                            {s.count} balle{s.count > 1 ? 's' : ''}
                                            {s.title && ` · ${s.title}`}
                                        </>
                                    )}
                                    {sessionDuration(s.startedAt, s.endedAt)
                                        && ` · ${sessionDuration(s.startedAt, s.endedAt)}`}
                                    {s.status !== 'completed' && s.status !== 'finished'
                                        && ` · ${STATUS_LABELS[s.status] ?? s.status}`}
                                    {s.location && ` · ${s.location}`}
                                </div>

                                {/* Le mémo du mode libre. Sur une ligne à part et
                                    en italique : c'est la phrase du joueur, pas
                                    une donnée de l'application, et c'est elle qui
                                    distingue deux séances libres. */}
                                {s.notes && (
                                    <div className="mt-1 truncate text-xs italic opacity-50">
                                        « {s.notes} »
                                    </div>
                                )}
                            </div>

                            <span className="shrink-0 text-right">
                                {putting ? (
                                    s.avgPutts != null && (
                                        <>
                                            <span className="block text-xl font-bold tabular-nums text-primary">
                                                {fr(s.avgPutts, 2)}
                                            </span>
                                            <span className="block text-[10px] uppercase opacity-50">
                                                coups/trou
                                            </span>
                                        </>
                                    )
                                ) : (
                                    s.successRate != null && (
                                        <span className="text-xl font-bold tabular-nums text-primary">
                                            {fr(s.successRate)}%
                                        </span>
                                    )
                                )}
                            </span>
                        </Link>
                    );
                })}
            </div>
        </div>
    );
}
